import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar';
import { FullScreenDialogService } from 'src/app/services/full-screen-dialog.service';
import { SECFormDocument } from '../sec-documents-viewer/sec-documents-viewer.component';
import { BackEndService } from 'src/app/services/backend.service';
import { exhibitCheckboxes } from '../filtering-search-component-base/filtering-search-common-definitions';
import Mark from 'mark.js'
import { environment } from 'src/environments/environment'
import { DatePipe, DOCUMENT } from '@angular/common';
import { SafePipe } from 'safe-pipe';
import { FormsModule } from '@angular/forms';
import { NgxBootstrapIconsModule, IconNamesEnum } from 'ngx-bootstrap-icons';
import { AdobePDFViewerComponent } from '../adobe-pdf-viewer/adobe-pdf-viewer';

@Component({
  selector: 'sec-document-viewer',
  imports: [
    NgScrollbarModule,
    NgxBootstrapIconsModule,
    SafePipe,
    FormsModule,
    NgbTooltipModule,
    AdobePDFViewerComponent
],
  templateUrl: './sec-document-viewer.component.html',
  styleUrl: './sec-document-viewer.component.scss',
  providers: [
  ],
})
export class SECDocumentViewerComponent {
  public iconNames = IconNamesEnum
  public modalRef: NgbActiveModal | undefined

  public displayingDocument = false
  public inSearchMode = false
  public searchLoading = false
  public searchResultIndex = 0
  public searchProgress = ''
  public currentForm: SECFormDocument | undefined

  private marks: NodeListOf<HTMLElement> | undefined
  private viewingExhibit = false
  private document = inject(DOCUMENT)

  public markjsConfig = {
    iframes: true,
    debug: true
  }


  private loadingHTML = `
<div class="loading-screen-parent">
  <div class="fade">
  </div>
  <div class="progress-container">
    <div class="progress">
      <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-label="Animated striped example" aria-valuenow="75" aria-valuemin="0" aria-valuemax="100" style="width: 100%"></div>
    </div>
  </div>
</div>
`

  public htmlContent = this.loadingHTML
  public tableOfContents: any[] = []
  public exhibits: any[] = []
  private mainDocumentHTML = ''
  public dialogTitle = ''
  public exportDownloadURL = ''
  public searchText = ''


  @ViewChild('secModal') secModal: any
  @ViewChild('document') documentContainer!: ElementRef
  @ViewChild('secdocument') scrollable: NgScrollbar = {} as NgScrollbar


  private modalService = inject(FullScreenDialogService)
  private backEndService = inject(BackEndService)

  private exhibitTypeLookup: { [x: string]: string } = {}

  constructor() {
    for (const exhibit of exhibitCheckboxes) {
      const parts = exhibit.split(':')
      this.exhibitTypeLookup[parts[0]] = parts[1]
    }
  }

  private resetUI() {
      this.resetSearch()
      this.htmlContent = ''
      this.exhibits = []
      this.tableOfContents = []
      this.modalRef = this.modalService.open(this.secModal, {
        size: 'xl',
        backdrop: 'static',
        keyboard: true,
        fullscreen: true,
      })
  }

  public displayDocumentViaFormIDAndDocID(formID: number, docID: number) {
    this.resetUI()
    this.currentForm = {
      formid: formID,
      documentid: docID,
      entity: {
        name: '',
        id: '',
      },
      documenttype: '',
      itemupdatedat: (new Date()).toDateString(),
      description: '',
      formname: '',
      formnameDescription: '',
      documentfilename: '',
      matched_documents: [],
      id: 0,
      snippets: ''
    }
    this.loadDocumentIntoDialog(formID, docID)
  }


  public displaySECDocument(form: SECFormDocument) {
      this.currentForm = form
      this.resetUI()
      if (form.entity.name.length > 0) {
        this.dialogTitle = form.entity.name + ': ' + form.documenttype + ' ' + (new DatePipe("en-US")).transform(form.itemupdatedat, 'short')
      } else {
        this.dialogTitle = ''
      }

    this.loadDocumentIntoDialog(form.formid, form.documentid, true)
  }

  addExhibitTooltips(exhibits: any[]) {
    exhibits.forEach((exhibit) => {
      exhibit.tooltip = ''
      if (exhibit.type.includes('EX-')) {
        exhibit.tooltip = this.exhibitTypeLookup[exhibit.type.split('.')[0]] ?? ''
      }
    })
    return exhibits
  }

  searchDocument(event: Event) {
    event.preventDefault()
    this.inSearchMode = false
    this.searchLoading = true
    const markInstance = new Mark(this.documentContainer.nativeElement)

    if (this.searchText.length > 2) {
      setTimeout(
        () =>
          markInstance.unmark({
            done: () => {
              this.searchLoading = false
              markInstance.mark((this.searchText || ''), this.markjsConfig)
              this.marks = this.document.querySelectorAll('mark')
              if (this.marks!.length > 0) {
                this.inSearchMode = true
                this.searchResultIndex = 0
                this.gotoMark(0)
              }
            }
          }), 10
      )
    }
  }

  gotoMark(delta: number) {
    this.searchResultIndex = this.searchResultIndex + delta
    if (this.searchResultIndex < 0) {
      this.searchResultIndex = this.marks!.length - 1
    } else if (this.searchResultIndex === this.marks!.length) {
      this.searchResultIndex = 0
    }
    this.marks![this.searchResultIndex].scrollIntoView({
      behavior: 'smooth'
    })
    this.searchProgress = `${this.searchResultIndex + 1}/${this.marks?.length}`
  }

  resetSearch() {
    this.searchLoading = false
    this.inSearchMode = false
    this.searchText = ''
    this.searchResultIndex = 0
    this.searchProgress = ''
  }



  loadDocumentIntoDialog(formID: number, docID: number, loadAll = true, exhibit = false) {
    this.viewingExhibit = exhibit
    this.htmlContent = this.loadingHTML
    this.backEndService.getSECDocument(formID, docID).subscribe((response: any) => {
      this.htmlContent = response.html
      if (loadAll) {
        this.tableOfContents = response.toc
        this.exhibits = this.addExhibitTooltips(response.exhibits)
        this.mainDocumentHTML = response.html
      }
      this.displayingDocument = true


      setTimeout(
        () => {
          this.documentContainer.nativeElement.querySelectorAll('a').forEach((element: HTMLElement) => {
            element.removeEventListener('click', this.documentClickHandler(element))
            element.addEventListener('click', this.documentClickHandler(element))
          })
          this.documentContainer.nativeElement.scrollTop = 0
          if (this.searchText.length > 0) {
            this.searchDocument(new Event('click'))
          }
        }, 10
      )
    })
  }

  documentClickHandler(element: Element) {
    return (event: Event) => {
      event.preventDefault()
      const href = element.getAttribute("href")
      if (href) {
        if (href.includes('https://www.sec.gov')) {
          this.backEndService.getSECURL(href).subscribe((response: any) => {
            this.htmlContent = response.html
            this.tableOfContents = response.toc
            this.exhibits = this.addExhibitTooltips(response.exhibits)
            setTimeout(
              () => {
                this.documentContainer.nativeElement.querySelectorAll('a').forEach((element: HTMLElement) => {
                  element.removeEventListener('click', this.documentClickHandler(element))
                  element.addEventListener('click', this.documentClickHandler(element))
                })
                this.scrollable.scrollTo({ top: 0 })
              }, 10
            )
          })
        } else if (href.includes('#')) {
          this.scrollToAnchor(href.substring(1))
        } else {
          for (const exhibit of this.exhibits) {
            if (exhibit.filename === href) {
              this.loadDocumentIntoDialog(this.currentForm!.formid, exhibit.id, false, true)
              break
            }
          }
        }
      }
    }
  }

  scrollToAnchor(anchor: string) {
    if (this.viewingExhibit) {
      this.viewingExhibit = false
      this.htmlContent = this.mainDocumentHTML
    }
    setTimeout(
      () => this.document?.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' }), 10
    )
  }

    downloadDocument(form: SECFormDocument, docID: number) {
      this.dialogTitle = form.entity.name + ': ' + form.documenttype + (new DatePipe("en-US")).transform(form.itemupdatedat, 'short')
      this.exportDownloadURL = environment.baseUrl + `secdocuments/${form.formid}/${docID}`
    }

}